import { useContext, useEffect, useMemo } from 'react';
import ConfigContext from '../contexts/ConfigContext';
import useMenuDirection from './useMenuDirection';
import usePortalHook from './usePortal';

const useMenu = ({
  usePortal,
  openFromRight,
  menuOffset = {},
  portalId = 'select',
  onClose,
  scrollingContainer,
  useMinWidthAsMaxWidth = false,
  menuGap = 0
}) => {
  const { isRtl } = useContext(ConfigContext);

  const { Portal, clickRef: wrapperRef, isOpen, setOpen, setClose, setToggle, coordinates } = usePortalHook({
    enabled: usePortal,
    portalId,
    onClose
  });

  const {
    menuRef,
    direction,
    containerDimensions: { height: containerHeight, width: containerWidth, left: containerLeft },
    menuDimensions: { height: menuHeight, width: menuWidth }
  } = useMenuDirection({ wrapperRef, dependencies: [isOpen] });

  const style = useMemo(() => {
    if (!usePortal) {
      const base = {
        position: 'absolute',
        minWidth: containerWidth,
        maxWidth: useMinWidthAsMaxWidth ? containerWidth : undefined,
        right: openFromRight ? menuOffset.right || 0 : undefined,
        left: openFromRight ? undefined : menuOffset.left || 0
      };
      return direction === 'up'
        ? { ...base, bottom: containerHeight + (menuOffset.top || 0) + menuGap }
        : { ...base, top: containerHeight + (menuOffset.top || 0) + menuGap };
    }

    const location = (condition) => {
      const windowWidth = window.innerWidth;
      let position = condition
        ? containerLeft - menuWidth + containerWidth + (menuOffset.left || 0)
        : containerLeft + (menuOffset.left || 0);

      // Prevent menu from going off screen
      if (position + menuWidth > windowWidth) {
        position = windowWidth - menuWidth - 10; // 10px padding from right edge
      }
      if (position < 0) {
        position = 10; // 10px padding from left edge
      }

      return position;
    };

    // Vertical counterpart of location(): prevent the menu from going off screen.
    const verticalLocation = () => {
      const windowHeight = window.innerHeight;
      // top is absolute here, so the gap moves up above the container and down below it
      let top =
        direction === 'up'
          ? coordinates?.y - menuHeight + (menuOffset.top || 0) - menuGap
          : coordinates?.y + containerHeight + (menuOffset.top || 0) + menuGap;

      if (top + menuHeight > windowHeight) {
        top = windowHeight - menuHeight - 10; // 10px padding from bottom edge
      }
      if (top < 0) {
        top = 10; // 10px padding from top edge
      }

      return top;
    };

    const base = {
      position: 'fixed',
      minWidth: containerWidth,
      maxWidth: useMinWidthAsMaxWidth ? containerWidth : undefined,
      left: isRtl ? undefined : location(openFromRight),
      right: !isRtl ? undefined : location(!openFromRight)
    };

    return { ...base, top: verticalLocation() };
  }, [
    containerHeight,
    containerLeft,
    containerWidth,
    coordinates?.y,
    direction,
    isRtl,
    menuGap,
    menuHeight,
    menuOffset.left,
    menuOffset.right,
    menuOffset.top,
    menuWidth,
    openFromRight,
    useMinWidthAsMaxWidth,
    usePortal
  ]);

  useEffect(() => {
    if (usePortal && scrollingContainer) {
      const node = scrollingContainer.current;
      node.addEventListener('scroll', setClose);
      return () => node.removeEventListener('scroll', setClose);
    }
  }, [scrollingContainer, setClose, usePortal]);

  return { Portal, wrapperRef, isOpen, setOpen, setClose, setToggle, style, menuRef };
};

export default useMenu;
